import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FormattedMessage } from 'react-intl';
import { getErrorMessageKey } from 'myParkdUtils/errorRendering';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUnlockAlt } from '@fortawesome/free-solid-svg-icons';
import { usePrevious } from 'myParkdUtils/customHooks';
import * as validation from 'myParkdUtils/validationHandling';
import SubmitButton from 'components/SubmitButton';
import ErrorMessage from 'components/ErrorMessage';
import PasswordInputFieldWithToggle from 'screens/settings/PasswordInputFieldWithToggle';
import { formatMessage } from 'i18n/CurrentLocale';
import { authSlice } from 'features/auth/authSlice';
import { ReduxState } from 'types';


type ValidationErrors = {
    oldPassword?: any,
    newPassword?: any
}

export const ChangePasswordForm = (props: Props) => {
    const updatePasswordStatus = props.updatePasswordStatus;
    const previousUpdatePasswordStatus = usePrevious(updatePasswordStatus);
    const passwordsForm = useRef(null);

    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);

    useEffect(() => {
        if (
            previousUpdatePasswordStatus &&
            previousUpdatePasswordStatus.loading &&
            (updatePasswordStatus.succeeded || updatePasswordStatus.error)
        ) {
            setFormSubmitted(true);
        }
    }, [updatePasswordStatus]);

    const getValidationErrors = () => {
        let errors: ValidationErrors = {};
        errors.oldPassword = validation.checkForMinimumLength(oldPassword, 6);
        errors.newPassword = validation.checkForMinimumLength(newPassword, 6);
        return errors;
    };

    const handleSubmit = (e: any) => {
        e.preventDefault();
        const validationSuccessful = validation.validate(getValidationErrors());
        if (validationSuccessful) {
            props.updatePassword({ oldPassword, newPassword });
            passwordsForm.current.reset();
            document.activeElement.blur();
        } else {
            setShowValidationErrors(true);
        }
    };

    const errors = getValidationErrors();
    const validationSuccessful = validation.validate(errors);

    return (
        <div className="radius-rectangle settings-box">
            <div className="settings-box-header">
                <h3 className="bold-title">
                    <FontAwesomeIcon icon={faUnlockAlt} className="icon-before" />
                    <FormattedMessage id="SETTINGS_CHANGE_YOUR_PASSWORD" />
                </h3>
            </div>
            <div className="settings-box-content">
                <form
                    ref={passwordsForm}
                    onSubmit={handleSubmit}
                    noValidate
                    className={
                        'full-width-form ' +
                        (validationSuccessful ? 'validation-successful ' : '') +
                        (showValidationErrors ? 'show-validation-messages' : 'hide-validation-messages')
                    }
                >

                    <div className="row-7">
                        <div className="col-6">
                            <label htmlFor="oldPassword">
                                <span>
                                    <FormattedMessage id="SETTINGS_CURRENT_PASSWORD" />
                                </span>
                            </label>
                            <PasswordInputFieldWithToggle
                                id="oldPassword"
                                error={errors?.oldPassword}
                                onPasswordChange={setOldPassword}
                            />
                            <ErrorMessage msg={errors?.oldPassword} />
                        </div>

                        <div className="col-6">
                            <label htmlFor="newPassword">
                                <span>
                                    <FormattedMessage id="SETTINGS_NEW_PASSWORD" />
                                </span>
                            </label>
                            <PasswordInputFieldWithToggle
                                id="newPassword"
                                error={errors?.newPassword}
                                onPasswordChange={setNewPassword}
                            />
                            <ErrorMessage msg={errors?.newPassword} />
                        </div>
                    </div>


                    {formSubmitted && (
                        <React.Fragment>
                            {updatePasswordStatus.error && (
                                <div className="error-box">
                                    <FormattedMessage id={getErrorMessageKey(updatePasswordStatus.error)} />
                                </div>
                            )}
                            {updatePasswordStatus.succeeded && (
                                <div className="success-box">
                                    <FormattedMessage id="SETTINGS_PASSWORD_UPDATE_SUCCESS" />
                                </div>
                            )}
                        </React.Fragment>
                    )}
                    <SubmitButton buttonText={formatMessage({ id: 'SETTINGS_SAVE_PASSWORD' })} saving={updatePasswordStatus.loading} />
                </form>
            </div>
        </div>
    );
};


type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = StateProps & DispatchProps;

function mapStateToProps(state: ReduxState) {
    return {
        updatePasswordStatus: state.auth.updatePasswordStatus,
    };
}

function mapDispatchToProps(dispatch: any) {
    return bindActionCreators({
        updatePassword: authSlice.actions.changePassword,
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(ChangePasswordForm);